import { useState } from "react";
import { getExtractionJobsStatus, type SupportedSourceConnector } from "../../lib/corpusApi";
import { usePolling } from "../../hooks/usePolling";

const POLL_INTERVAL_MS = 3_000;
const MAX_CONSECUTIVE_ERRORS = 3;

export interface ExtractionWarmupState {
  warm: boolean;
  /** Null until the first successful status check (or whenever warm is false). */
  sourceConnectors: SupportedSourceConnector[] | null;
}

export interface ExtractionWarmupOptions {
  /** Whether to check at all. Checking wakes the backend, so a caller that only sometimes needs it
   *  turns this on only while someone might act on it. Once warm, it stays warm. Default true. */
  enabled?: boolean;
}

/**
 * Tracks whether the extraction backend is warm. Used by the internal pages that send work to it
 * — URL submission, and validating a PubMed query — and deliberately not app-wide, since knowing
 * this is only useful right before someone might act. Starts checking (and thus waking the
 * backend) as soon as the page or form that needs it appears, so warmth is more likely already
 * known by the time a curator acts. Polls every 3s only until the first warm result, then stops:
 * the status call waits out a cold start, so a warm result is durable, and the manifest it
 * carries is static — there's nothing left to poll for. If the backend later scales back to zero
 * the gate just stays open, which is fine (the action itself re-wakes it, and small batches run
 * inline regardless). Also carries the source-connector manifest (see SubmitUrlsDialog's
 * pre-submit URL warning).
 */
export function useExtractionWarmup({
  enabled = true,
}: ExtractionWarmupOptions = {}): ExtractionWarmupState {
  const [state, setState] = useState<ExtractionWarmupState>({
    warm: false,
    sourceConnectors: null,
  });

  usePolling(
    async () => {
      try {
        const { warm, sourceConnectors } = await getExtractionJobsStatus();
        setState({ warm, sourceConnectors });
      } catch (e) {
        setState({ warm: false, sourceConnectors: null });
        throw e;
      }
    },
    POLL_INTERVAL_MS,
    // Stop once warm — a warm result is durable and the manifest is static, so there's nothing
    // left to poll for.
    { maxConsecutiveErrors: MAX_CONSECUTIVE_ERRORS, enabled: enabled && !state.warm }
  );

  return state;
}
